import * as React from 'react';
import { Collapsible as CollapsiblePrimitive } from 'radix-ui';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Collapsible } from '@/components/ui/collapsible';

function Tree({ className, ...props }: React.ComponentProps<'ul'>) {
    return (
        <ul
            role="tree"
            data-slot="tree"
            className={cn('flex w-full flex-col gap-0.5 text-sm select-none', className)}
            {...props}
        />
    );
}

type TreeNodeProps = React.ComponentProps<typeof Collapsible> & {
    children: React.ReactNode;
};

function TreeNode({ className, children, defaultOpen, ...props }: TreeNodeProps) {
    return (
        <li role="treeitem" data-slot="tree-node" className={cn('flex flex-col', className)}>
            <Collapsible defaultOpen={defaultOpen} {...props}>
                {children}
            </Collapsible>
        </li>
    );
}

// NOTE: These wrappers use `React.forwardRef` rather than the plain-function /
// "ref-as-prop" style of the other shadcn components in this folder: they wrap
// radix Collapsible primitives, and on React 18 a plain function component cannot
// receive the ref radix forwards under `asChild`/Slot composition (see the note in
// dialog.tsx). The radix primitives are used directly here because the plain-function
// wrappers in collapsible.tsx cannot pass a ref through.
const TreeNodeTrigger = React.forwardRef<
    React.ElementRef<typeof CollapsiblePrimitive.CollapsibleTrigger>,
    React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleTrigger>
>(({ className, children, ...props }, ref) => (
    <CollapsiblePrimitive.CollapsibleTrigger
        ref={ref}
        data-slot="tree-node-trigger"
        className={cn(
            'group/tree-node-trigger flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            className,
        )}
        {...props}
    >
        <ChevronRight
            data-slot="tree-node-chevron"
            className="size-3.5 shrink-0 text-muted-foreground transition-transform duration-150 group-data-[state=open]/tree-node-trigger:rotate-90"
        />
        {children}
    </CollapsiblePrimitive.CollapsibleTrigger>
));
TreeNodeTrigger.displayName = 'TreeNodeTrigger';

const TreeNodeChildren = React.forwardRef<
    React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
    React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, children, ...props }, ref) => (
    <CollapsiblePrimitive.CollapsibleContent
        ref={ref}
        data-slot="tree-node-children"
        className={cn('overflow-hidden data-open:animate-in data-closed:animate-out', className)}
        {...props}
    >
        <ul role="group" className="ml-2.5 flex flex-col gap-0.5 border-l border-border pl-2.5 pt-0.5">
            {children}
        </ul>
    </CollapsiblePrimitive.CollapsibleContent>
));
TreeNodeChildren.displayName = 'TreeNodeChildren';

function TreeLeaf({ className, children, ...props }: React.ComponentProps<'li'>) {
    return (
        <li
            role="treeitem"
            data-slot="tree-leaf"
            className={cn(
                'flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-muted/50',
                className,
            )}
            {...props}
        >
            {children}
        </li>
    );
}

function TreeLabel({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="tree-label"
            className={cn('flex min-w-0 flex-1 items-center gap-2 truncate', className)}
            {...props}
        />
    );
}

function TreeActions({ className, onClick, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="tree-actions"
            className={cn('ml-auto flex shrink-0 items-center gap-1', className)}
            onClick={(event) => {
                event.stopPropagation();
                onClick?.(event);
            }}
            {...props}
        />
    );
}

export { Tree, TreeNode, TreeNodeTrigger, TreeNodeChildren, TreeLeaf, TreeLabel, TreeActions };
